/**
 * A LiveView hook that manages tooltips using Tippy.js. This hook provides a flexible way to
 * create and manage tooltips with customizable triggers and content.
 *
 * @typedef {Object} TooltipHook
 *
 * Dataset Attributes:
 * ------------------
 * - data-tooltip-max-width: (optional) Sets the maximum width of the tooltip
 * - data-tooltip-trigger-element-selector: (optional) CSS selector for the trigger element. Defaults to ".trigger"
 * - data-tooltip-element-selector: (optional) CSS selector for the tooltip content element. Defaults to ".tooltip"
 * - data-tooltip-arrow-disabled: (optional) When present, disables the tooltip arrow
 *
 * Example Usage:
 * -------------
 * ```html
 * <div
 *   id="my-tooltip"
 *   phx-hook="Tooltip"
 *   data-tooltip-max-width="300"
 * >
 *   <button class="trigger">Hover me</button>
 *   <div class="tooltip">
 *     <div>Tooltip content here</div>
 *   </div>
 * </div>
 * ```
 *
 * Lifecycle:
 * ---------
 * - mounted(): Initializes the Tippy instance with configured options
 * - updated(): Updates the tooltip content when the inner content changes
 * - destroyed(): Cleans up the Tippy instance when the element is removed
 */
import alpineFns from "../alpine_functions"

/**
 * Walks up from `el` to the nearest scrollable ancestor.
 *
 * Email tooltips are appended to document.body, so when the list they live in
 * scrolls, the tooltip detaches from its trigger and floats in the wrong place.
 * We hide it on that scroll. Returns null when there is no scrollable ancestor
 * (e.g. the tooltip is used outside a scrolling list), in which case there is
 * simply nothing to listen to.
 */
function findScrollableAncestor(el) {
  let node = el?.parentElement
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

const Tooltip = {
  mounted() {
    const maxWidth = this.el.dataset.tooltipMaxWidth
    const triggerElementSelector =
      this.el.dataset.tooltipTriggerElementSelector || ".trigger"
    const tooltipElementSelector =
      this.el.dataset.tooltipElementSelector || ".tooltip"
    const arrowDisabled = this.el.dataset.tooltipArrowDisabled
    const placement = this.el.dataset.tooltipPlacement || "top"
    const isEmailTooltip = this.el.dataset.tooltipIsEmail
    const offsetValue = this.el.dataset.tooltipOffset
    const noFlip = this.el.dataset.tooltipNoFlip === "true"
    const delay = this.el.dataset.tooltipDelay

    let opts = {
      arrow: true,
      placement: placement,
      appendTo: document.body, // Append to body to avoid clipping
      zIndex: 9999, // Ensure high z-index to appear above other elements
    }
    if (maxWidth) {
      opts.maxWidth = +maxWidth
    }
    if (arrowDisabled) {
      opts.arrow = false
    }

    if (offsetValue) {
      const [x, y] = offsetValue.split(",").map((val) => parseInt(val.trim()))
      opts.offset = [x, y]
    }

    if (delay) {
      opts.delay = [parseInt(delay), 0]
    }

    // Add popperOptions if preventing flip behavior is requested
    if (noFlip) {
      opts.popperOptions = {
        ...(opts.popperOptions || {}),
        modifiers: [
          ...((opts.popperOptions || {}).modifiers || []),
          {
            name: "flip",
            options: {
              fallbackPlacements: [], // Empty array prevents flipping
            },
          },
        ],
      }
    }

    if (isEmailTooltip) {
      opts = {
        ...opts,
        delay: [500, 0],
        offset: [10, 10],
        inlinePositioning: true,
        interactive: false,
        popperOptions: {
          strategy: "absolute",
          modifiers: [
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top", "bottom"],
              },
            },
          ],
        },
        onShow(instance) {
          const scrollContainer = findScrollableAncestor(instance.reference)
          if (!scrollContainer) return

          const hideOnScroll = () => instance.hide()
          scrollContainer.addEventListener("scroll", hideOnScroll, {
            passive: true,
          })

          instance.setProps({
            onHide() {
              scrollContainer.removeEventListener("scroll", hideOnScroll)
            },
          })
        },
      }
    }

    opts.triggerElement = this.el.querySelector(triggerElementSelector)
    opts.tooltipElement = this.el.querySelector(tooltipElementSelector)
    this.tooltipElement = opts.tooltipElement
    this.tippy = alpineFns.infoComponentInit(this.el, opts)

    if (this.tippy) {
      this.handleEvent("tooltip:hide", () => {
        this.tippy.hide()
      })
    }
  },

  updated() {
    // When the inner content of the tooltip hook's element changes, update the tippy content
    if (this.tippy && this.tooltipElement && this.tooltipElement.children[0]) {
      this.tippy.setContent(this.tooltipElement.children[0].cloneNode(true))
    }
  },

  destroyed() {
    if (this.tippy) {
      this.tippy.destroy()
    }
  },
}

export { Tooltip }
